// ════════════════════════════════════════════════════════════════════════════
// CardTarefaCompacto — r140 + r145 (mais informações)
// Versão compacta do card de tarefa, usado no painel esquerdo da tela de
// Agenda. É arrastável (drag&drop) pro calendário à direita.
// ════════════════════════════════════════════════════════════════════════════

import React from 'react';

const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  muted:   '#888',
  branco:  '#fff',
  verde:   '#1D9E75',
  // r178: verde exclusivo do status 'Em Andamento' (nao reaproveitar noutros
  //       elementos -- COR.verde segue valendo pro resto).
  verdeAndamento: '#009900',
  verdeBg: '#f0fff4',
  amarelo: '#d97706',
  // r182: amarelo do status 'Pendente de Retorno' (o COR.amarelo do Pausado
  //       e mais ambar -- os dois precisam ser distinguiveis lado a lado).
  amareloRetorno: '#eab308', amlRetBg: '#fefce8',
  amlBg:   '#fffbeb',
  vermelho:'#e53935',
  vermBg:  '#fff0f0',
  cinza:   '#9ca3af',
  // r178: cinza exclusivo do status 'Nao Iniciado' (COR.cinza segue valendo
  //       pro resto -- este e claro demais pra texto sobre branco).
  cinzaNaoIniciado: '#CCCCCC',
  cinzaSt: '#f3f4f6',
  laranja: '#f97316',
  laranjaBg: '#fff7ed',
  azulPB:  '#dbeafe',
  // r220: Pausado virou azul claro e Pendente Cham. Filho virou laranja.
  //       Chaves proprias — COR.azul e COR.laranja seguem valendo pro resto.
  azulClaroPausado: '#38BDF8', azulClaroPausadoBg: '#f0f9ff',
  laranjaFilho:     '#F97316', laranjaFilhoBg:     '#fff7ed',
};

// r145: mapa completo (label + cores) — espelha o que está em TarefasPageLista
const STATUS_EXECUCAO = {
  N: { label: 'Não Iniciado',          cor: COR.cinzaNaoIniciado,    bg: COR.cinzaSt   },
  E: { label: 'Em Andamento',          cor: COR.verdeAndamento,    bg: COR.verdeBg   },
  X: { label: 'Pausado',               cor: COR.azulClaroPausado, bg: COR.azulClaroPausadoBg },
  R: { label: 'Pendente de Retorno',   cor: COR.amareloRetorno, bg: COR.amlRetBg },
  P: { label: 'Pendente Cham. Filho',  cor: COR.laranjaFilho, bg: COR.laranjaFilhoBg },
  F: { label: 'Finalizado',            cor: COR.vermelho, bg: COR.vermBg    },
  S: { label: 'Sandbox',               cor: COR.laranja,  bg: COR.laranjaBg },
};

const SETOR = {
  C: { label: 'Comercial',     cor: '#2563eb' },
  T: { label: 'Técnico',       cor: '#7c3aed' },
  Q: { label: 'Qualidade',     cor: '#f97316' },
  A: { label: 'Administrativo',cor: '#6b7280' },
  P: { label: 'Apoio Técnico',  cor: '#db2777' },
  O: { label: 'Outros',        cor: '#06b6d4' },
};

const PRIORIDADE = {
  A: { label: 'Alta',  cor: COR.vermelho, icone: '▲' },
  M: { label: 'Média', cor: COR.amarelo,  icone: '◆' },
  B: { label: 'Baixa', cor: COR.verde,    icone: '▼' },
};

// ─── Pílula compacta padronizada ────────────────────────────────────────────
function Pill({ texto, cor, bg, icone, titulo }) {
  return (
    <span title={titulo} style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      padding: '1px 6px', borderRadius: '8px',
      fontSize: '9px', fontWeight: '600',
      backgroundColor: bg || 'transparent',
      color: cor,
      border: `1px solid ${cor}`,
      whiteSpace: 'nowrap',
    }}>
      {icone && <span>{icone}</span>}
      {texto}
    </span>
  );
}

function TagCliente({ tag, cor, titulo }) {
  if (!tag) return null;
  const hex = (cor || COR.azul).replace('#','');
  const r = parseInt(hex.substr(0,2),16);
  const g = parseInt(hex.substr(2,2),16);
  const b = parseInt(hex.substr(4,2),16);
  const lum = (0.299*r + 0.587*g + 0.114*b) / 255;
  return (
    <span title={titulo || tag} style={{
      display: 'inline-block', padding: '1px 6px',
      borderRadius: '8px', fontSize: '9px', fontWeight: '600',
      backgroundColor: cor || COR.azul, color: lum > 0.5 ? '#1a1a2e' : '#fff',
    }}>{tag}</span>
  );
}

function CardTarefaCompacto({ tarefa, onClick, temPlanejada, temApontamento }) {
  const status = STATUS_EXECUCAO[tarefa.status_execucao] || STATUS_EXECUCAO.N;
  const setor  = SETOR[tarefa.setor] || SETOR.O;
  const prio   = PRIORIDADE[tarefa.prioridade] || PRIORIDADE.M;

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/tarefa-id', tarefa.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={() => onClick && onClick(tarefa)}
      style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`,
        borderLeft: `4px solid ${status.cor}`,   // r145: cor da borda = status
        borderRadius: '6px',
        padding: '8px 10px',
        marginBottom: '8px',
        cursor: 'grab',
        transition: 'box-shadow 0.15s',
        display: 'flex', flexDirection: 'column', gap: '5px',
        fontFamily: "'Manrope', sans-serif",
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 2px 8px ${COR.borda}`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Linha 1: status • #id • título + horas */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{
          width: '8px', height: '8px', borderRadius: '50%',
          backgroundColor: status.cor, flexShrink: 0,
        }} />
        <span style={{ fontSize: '11px', color: COR.muted, fontWeight: '500' }}>
          #{tarefa.id}
        </span>
        <span style={{ fontSize: '12px', fontWeight: '600', color: COR.texto,
                       flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tarefa.titulo}
        </span>
        {tarefa.horas_estimadas && (
          <span style={{ fontSize: '10px', color: COR.azul, fontWeight: '700', whiteSpace: 'nowrap' }}>
            ⏱ {parseFloat(tarefa.horas_estimadas).toFixed(1)}h
          </span>
        )}
      </div>

      {/* Linha 2: tag cliente + projeto */}
      <div style={{ marginLeft: '14px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        {tarefa.cliente_tag && (
          <TagCliente tag={tarefa.cliente_tag} cor={tarefa.cliente_cor_tag}
                      titulo={tarefa.cliente_fantasia || tarefa.cliente_nome} />
        )}
        {tarefa.negocio_id && (
          <span style={{ fontSize: '10px', color: COR.muted, overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📁 {tarefa.negocio_id}
            {tarefa.negocio_descricao && ` — ${tarefa.negocio_descricao}`}
          </span>
        )}
      </div>

      {/* r146: Linha 3 — pílulas + pessoas tudo junto, com flex-wrap */}
      <div style={{
        marginLeft: '14px', paddingTop: '3px',
        borderTop: `1px dashed ${COR.borda}`,
        display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
      }}>
        <Pill texto={status.label} cor={status.cor} bg={status.bg} titulo="Status" />
        <Pill texto={setor.label}  cor={setor.cor}                titulo="Setor" />
        <Pill texto={prio.label}   cor={prio.cor}  icone={prio.icone} titulo="Prioridade" />
        {/* r146: pílulas que indicam alocação no calendário */}
        {temPlanejada && (
          <Pill texto="Planejado" cor={COR.azul} bg={COR.azulBg} icone="📅"
                titulo="Tarefa tem planejamento no seu calendário" />
        )}
        {temApontamento && (
          <Pill texto="Apontado" cor={COR.verde} bg={COR.verdeBg} icone="⏱"
                titulo="Tarefa tem horas apontadas" />
        )}
        {/* Pessoas inline */}
        {tarefa.solicitante_nome && (
          <span title="Solicitante (proprietário)" style={{ fontSize: '10px', color: COR.muted, marginLeft: '4px' }}>
            👤 <b style={{ color: COR.texto }}>{tarefa.solicitante_nome}</b>
          </span>
        )}
        {tarefa.responsavel_nome && (
          <span title="Responsável" style={{ fontSize: '10px', color: COR.muted }}>
            👥 <b style={{ color: COR.texto }}>{tarefa.responsavel_nome}</b>
          </span>
        )}
      </div>
    </div>
  );
}

export default CardTarefaCompacto;
